// @ts-strict-ignore
import React, { KeyboardEvent, ReactNode, useCallback, useEffect, useState } from 'react';
import isHotkey from 'is-hotkey';
import { Editable, ReactEditor, Slate } from 'slate-react';
import { Editor, Transforms, Range, Descendant } from 'slate';
import { Leaf } from 'src/components/textEditor/components/leaf';
import { Element } from 'src/components/textEditor/components/element';
import MentionsPortal from 'src/components/textEditor/components/suggestedMentionsPortal';
import { MyEditor } from 'src/components/textEditor/customHelpers';
import { HOTKEYS } from 'src/constants/write';
import { useFetchCommunityMentions } from 'src/utils/hooks/useFetchMentions';

// this is a powerful, extensible rich text editor that does not play nice with all androids - can be used for desktop, ios.
// if used with andriods, issues with handling predictive text inputs on older phones + new slate versions do not account for Firefox's unique input handling

type Props = {
  autoFocus?: boolean;
  children?: ReactNode;
  editor: Editor;
  hasHotkeyFormatting?: boolean;
  hasMentions?: boolean;
  imgWithDescription?: boolean;
  initialValue?: Descendant[];
  maxChars?: number;
  placeholder: string;
  toolbar?: ReactNode;
  onChange?: (editor: Editor) => void;
  onFocus?: () => void;
  onKeyDown?: (event: KeyboardEvent, editor?: Editor) => void;
  onKeyUp?: (editor: Editor) => void;
};
export default function RichTextEditor({
  autoFocus = true,
  children = null,
  editor,
  // pass editor with any plugins you need
  hasHotkeyFormatting = true,
  hasMentions = true,
  imgWithDescription = true,
  initialValue,
  maxChars,
  placeholder,
  // COMPAT: placeholder must end with a /n or ... - otherwise, some autocorrect/text predictions pick it up.
  toolbar = null,
  onChange,
  onFocus,
  onKeyDown,
  onKeyUp
}: Props): JSX.Element {
  const defaultValue: Descendant[] = initialValue || [{
    type: 'paragraph',
    children: [{
      text: ''
    }]
  }];
  const renderElement = useCallback(({
    attributes,
    children,
    element
  }) => <Element attributes={attributes} element={element} imgWithDescription={imgWithDescription}>{children}</Element>, []);
  const renderLeaf = useCallback(({
    attributes,
    children,
    leaf
  }) => <Leaf attributes={attributes} leaf={leaf}>{children}</Leaf>, []);

  // Mentions
  const [target, setTarget] = useState<Range | undefined>();
  const [index, setIndex] = useState(0);
  const {
    suggestedMentions,
    setSearchText
  } = useFetchCommunityMentions();
  const insertMention = (index: number) => {
    Transforms.select(editor, target);
    const canAdd = MyEditor.canAddMention(editor);
    // @ts-expect-error exec needs editor specified
    if (canAdd) editor.exec({
      type: 'insert_mention',
      mentionedUser: suggestedMentions[index]
    });
    setTarget(null);
  };
  const setCurrentMentionIndex = (newIndex: number) => {
    newIndex = newIndex % suggestedMentions.length;
    if (newIndex < 0) newIndex = suggestedMentions.length + newIndex; // loop through the options
    setIndex(newIndex);
  };
  const navigateSuggestedMentions = useCallback(event => {
    if (target) {
      switch (event.key) {
        case 'ArrowDown':
          {
            event.preventDefault(); // else it will jump back to the editor
            setCurrentMentionIndex(index + 1);
            break;
          }
        case 'ArrowUp':
          {
            event.preventDefault();
            setCurrentMentionIndex(index - 1);
            break;
          }
        case 'Enter':
          {
            event.preventDefault();
            if (suggestedMentions.length > 0) insertMention(index);else setTarget(null); // handle mentions that don't exist
            break;
          }
        case 'Tab':
        case 'Escape':
          event.preventDefault();
          setTarget(null);
          break;
      }
    }
  }, [index, target, suggestedMentions]);
  const handleHotKeys = event => {
    for (const hotkey in HOTKEYS) {
      if (isHotkey(hotkey, event)) {
        event.preventDefault();
        const mark = HOTKEYS[hotkey];
        const isActive = MyEditor.isMarkActive(editor, mark);
        MyEditor.toggleMarkToolbar(editor, mark, isActive); // for toggling text - bold, italics, underline..
      }
    }
  };
  const [isFocused, setIsFocused] = useState(ReactEditor.isFocused(editor));

  // any future editor should have autofocus on to go to end of content on edit! use only 1 autofocus editor for multi-editor pages
  useEffect(() => {
    if (autoFocus && MyEditor.getString(editor, [])?.length > 1) {
      // move editor focus to end of editor content. Also triggers on-change, i.e. validation
      Transforms.select(editor, Editor.end(editor, []));
    } else {
      // set editor selection at start, as well as prevent onchange to trigger on focus
      editor.selection = {
        anchor: {
          path: [0, 0],
          offset: 0
        },
        focus: {
          path: [0, 0],
          offset: 0
        }
      };
    }
  }, []);
  return <>
      <Slate editor={editor} initialValue={defaultValue} onChange={() => {
      if (hasMentions) MyEditor.captureMention(editor, setTarget, setSearchText, setIndex);
      if (onChange) onChange(editor);
    }} data-sentry-element="Slate" data-sentry-source-file="richTextEditor.tsx">
        <Editable
      // spellCheck - breaks in firefox and motorolas because of how their spellcheck is implemented
      autoFocus={autoFocus} placeholder={placeholder} renderElement={renderElement} renderLeaf={renderLeaf} onFocus={() => {
        if (onFocus) onFocus();
      }} onKeyDown={event => {
        if (onKeyDown) onKeyDown(event, editor);
        if (hasHotkeyFormatting) handleHotKeys(event);
        if (hasMentions) navigateSuggestedMentions(event);
        MyEditor.imposeTextLimit(editor, event, maxChars);
      }} onKeyUp={event => {
        // this is a safari hack for tabbing between editors
        // tabbed focus acquisition not working on setEditor - editor gets set to focused, but focus not visible (in chrome and in code works fine)
        if (event.key === 'Tab') {
          setIsFocused(!isFocused);
        }
        if (onKeyUp) onKeyUp(editor);
      }} data-sentry-element="Editable" data-sentry-source-file="richTextEditor.tsx" />
        {children}
        {hasMentions && target && suggestedMentions.length > 0 && <MentionsPortal addMention={insertMention} editor={editor} index={index} setIndex={i => setIndex(i)} target={target} users={suggestedMentions} />}
        {toolbar}
      </Slate>
    </>;
}